// AUTH V2
import { RootState } from "../../reduxStore/store"
import { createSelector } from "@reduxjs/toolkit"
import {
  PracticeRole,
  PracticeRoleEnum,
  UserRole,
} from "../../domain/Account/userSlice"
import { PracticeStatus } from "../../domain/Practice/schema"
import { ProviderStatusDisplayId } from "../../domain/Provider/schema"
import { selectPractice } from "../../domain/Practice/model"
import { selectProvider } from "../../domain/Provider/model"
import { selectUser } from "../shared/selectors"

// Type Semantics
export type Claim = (state: RootState) => boolean // Fact to satisty authorization
export type ClaimSet = Claim[] //  A collection of claims that must all be satisfied
export type Policy = ClaimSet[] // A collection of claim sets, where satisfying any one set satisfies the policy

// AuthZ Mechanics
export const satisfiesClaims = (claims: Claim[]) =>
  createSelector([(state: RootState) => state], (state) => {
    return claims.every((claim) => claim(state))
  })
export const satisfiesPolicy = (policy: Policy) =>
  createSelector([(state: RootState) => state], (state) => {
    return policy.some((claims) => satisfiesClaims(claims)(state))
  })

// Useful Claim Constructors
export const hasPracticeRole =
  (role: PracticeRole): Claim =>
  (state: RootState) => {
    return state.user.practiceRoles.some((practiceRole) => {
      return (
        practiceRole.roles.includes(role) &&
        practiceRole.practiceId == state.practice.practice.id
      )
    })
  }

export const affiliateHasPracticeRole =
  (role: PracticeRoleEnum): Claim =>
  (state: RootState) => {
    const selectProviderAffiliate = (state: RootState) => {
      const practice = selectPractice(state)
      const provider = selectProvider(state)
      return practice.practiceAffiliates?.find(
        (affiliate) => affiliate.userId === provider.userId,
      )
    }
    const affiliate = selectProviderAffiliate(state)
    return !!affiliate?.roleDisplayIds.includes(role)
  }

export const hasUserRole =
  (role: UserRole): Claim =>
  (state: RootState) => {
    return state.user.roles.includes(role)
  }

export const hasPracticeStatus =
  (status: PracticeStatus): Claim =>
  (state: RootState) => {
    return state.practice.practice.status == status
  }

export const hasProviderStatus =
  (status: ProviderStatusDisplayId): Claim =>
  (state: RootState) => {
    return state.provider.provider.providerStatusDisplayId == status
  }

export const isUserProvider = (state: RootState) => {
  const user = selectUser(state)
  const provider = selectProvider(state)
  return !!user.id && !!provider?.userId && user.id === provider.userId
}

export const notClaim = (claim: Claim): Claim => {
  return (state: RootState) => !claim(state)
}

// Useful High-Level Claims
export const isAffiliateRelations = hasUserRole("role-affiliate-relations")
export const isEapResearch = hasUserRole("role-eap-research")
export const isHAAdmin = hasUserRole("role-ha-admin")
export const isAffiliate = hasUserRole("role-affiliate")
export const isSecureCase = hasUserRole("role-secure-case")

export const isEapBilling = hasUserRole("role-eap-billing")

export const isPracticeManager = hasPracticeRole("practice-manager")
export const isPracticeAdmin = hasPracticeRole("practice-admin")
export const isPracticeBilling = hasPracticeRole("practice-billing")
export const isPracticeProvider = hasPracticeRole("provider")

export const providerAffiliateIsPracticeManager = affiliateHasPracticeRole(
  PracticeRoleEnum.PRACTICE_MANAGER,
)
export const providerAffiliateIsPracticeAdmin = affiliateHasPracticeRole(
  PracticeRoleEnum.PRACTICE_ADMIN,
)
export const providerAffiliateIsPracticeBilling = affiliateHasPracticeRole(
  PracticeRoleEnum.PRACTICE_BILLING,
)
export const providerAffiliateIsPracticeProvider = affiliateHasPracticeRole(
  PracticeRoleEnum.PROVIDER,
)

export const isPendingPractice = hasPracticeStatus("pending")
export const isPendingActivationPractice =
  hasPracticeStatus("pending-activation")
export const isActivePractice = hasPracticeStatus("active")
export const isSuspendedPractice = hasPracticeStatus("suspended")
export const isDeactivatedPractice = hasPracticeStatus("deactivated")

export const isPendingProvider = hasProviderStatus("pending")
export const isPendingActivationProvider =
  hasProviderStatus("pending-activation")
export const isActiveProvider = hasProviderStatus("active")
export const isSuspendedProvider = hasProviderStatus("suspended")
export const isDeactivatedProvider = hasProviderStatus("deactivated")

// Useful Policies
